import { Link, useSearchParams } from 'react-router-dom'
import { RotateCw } from 'lucide-react'
import type { LeadStatsConversionDto, LeadStatsNumberDto, VenueLeadStatsDto } from '@lib/dto/lead-stats-numbers.dto'
import { leadStatsViewSchema, type LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import type { LeadStatsQuery } from '@lib/input/lead-stats.input'
import { calendarDaySchema } from '@lib/validators/temporal.validator'
import { useAuth } from '@/auth/AuthContext'
import { can, reachesCompany } from '@/auth/access'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Select } from '@/components/ui/select'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useRefreshLeadStats, useVenueLeadStats } from '@/hooks/useLeadStats'
import { formatAdDay, formatCount, formatMicros } from '@/lib/adFormat'
import { instantLabel } from '@/lib/instantLabel'
import { cn } from '@/lib/utils'
import { Change, changeOf, InfoNote, PeriodBar, StatCost, StatTile, StatValue, StillMoving, Trend, ViewToggle } from '@/components/stats/StatsPieces'

/** The panel's pipeline, view and period live in the page's address, so a link reopens them. */
function useLeadStatsQuery(): [LeadStatsQuery, (next: LeadStatsQuery) => void] {
  const [params, setParams] = useSearchParams()
  const view = leadStatsViewSchema.safeParse(params.get('view'))
  const period = calendarDaySchema.safeParse(params.get('period'))
  const pipeline = params.get('pipeline')
  const query: LeadStatsQuery = {
    ...(pipeline === null ? {} : { pipeline }),
    ...(view.success ? { view: view.data } : {}),
    ...(period.success ? { period: period.data } : {}),
  }
  function set(next: LeadStatsQuery): void {
    setParams(
      (current) => {
        const out = new URLSearchParams(current)
        for (const key of ['pipeline', 'view', 'period'] as const) {
          const value = next[key]
          if (value === undefined) out.delete(key)
          else out.set(key, value)
        }
        return out
      },
      { replace: true },
    )
  }
  return [query, set]
}

/** A conversion to one decimal: `14.3%`. */
function conversionPercent(fraction: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(fraction)
}

/** A conversion's change, in percentage points. */
function pointsOf(value: number | null, previous: number | null): { text: string; up: boolean | null } | null {
  if (value === null || previous === null) return null
  const diff = (value - previous) * 100
  if (Math.abs(diff) < 0.05) return { text: 'No change', up: null }
  return { text: `${Math.abs(diff).toFixed(1)} pts`, up: diff > 0 }
}

/**
 * How a number compares with the period before, or, in a period whose counting
 * began partway through (and so has nothing before it to compare with), the day
 * counting began.
 */
function NumberChange({ number, value }: { number: LeadStatsNumberDto; value: number }) {
  if (number.previous === null && number.countedFrom !== null) {
    return <p className="m-0 h-[17px] text-small text-ink-muted">Counted from {formatAdDay(number.countedFrom)}</p>
  }
  return (
    <Change
      change={changeOf(value, number.previous)}
      against={number.previous === null ? null : formatCount(number.previous)}
      sameAge={number.sameAge}
    />
  )
}

function NumberTile({ number, currency }: { number: LeadStatsNumberDto; currency: string | null }) {
  return (
    <StatTile label={number.name} stillMoving={number.stillMoving}>
      {number.value === null ? (
        <>
          <StatValue missing>—</StatValue>
          <p className="m-0 text-small text-ink-muted">
            {number.countedFrom === null ? 'Not counted yet' : `Counted from ${formatAdDay(number.countedFrom)}`}
          </p>
        </>
      ) : (
        <>
          <StatValue>{formatCount(number.value)}</StatValue>
          <NumberChange number={number} value={number.value} />
        </>
      )}
      <Trend points={number.trend} format={formatCount} label={number.name} />
      {currency === null ? null : (
        <StatCost>{number.costPerMicros === null ? 'No cost' : `${formatMicros(number.costPerMicros, currency)} each`}</StatCost>
      )}
    </StatTile>
  )
}

function ConversionRow({ conversion }: { conversion: LeadStatsConversionDto }) {
  return (
    <li className="flex flex-wrap items-center gap-3 border-t border-line py-2.5">
      <div className="min-w-0 flex-[1_1_10rem]">
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          {conversion.name}
          {conversion.stillMoving ? <StillMoving /> : null}
        </p>
      </div>
      <div className="text-right">
        <p className="m-0 text-lg font-semibold leading-6 text-ink">
          {conversion.value === null ? '—' : conversionPercent(conversion.value)}
        </p>
        <Change change={pointsOf(conversion.value, conversion.previous)} against={null} sameAge={false} />
      </div>
      <div className="w-24">
        <Trend points={conversion.trend} format={conversionPercent} label={conversion.name} />
      </div>
    </li>
  )
}

function Sources({ sources }: { sources: VenueLeadStatsDto['sources'] }) {
  const total = sources.reduce((n, s) => n + s.count, 0)
  const max = Math.max(1, ...sources.map((s) => s.count))
  return (
    <Card className="flex min-w-0 flex-[1_1_380px] flex-col gap-2.5 p-4">
      <div>
        <h3 className="m-0 text-sm font-semibold text-ink">Where leads came from</h3>
        <p className="m-0 text-xs text-ink-muted">{formatCount(total)} leads, by their source in GoHighLevel</p>
      </div>
      {sources.length === 0 ? <p className="m-0 text-sm text-ink-muted">No leads came in.</p> : null}
      {sources.map((source) => (
        <div
          key={source.name ?? '__rest'}
          className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_2rem] items-center gap-2.5 text-control"
        >
          <span className={cn('truncate', source.name === null && 'text-ink-muted')}>{source.name ?? 'Every other source'}</span>
          <div className="h-2.5">
            <div
              className={cn('h-2.5 rounded-r-sm', source.name === null ? 'bg-line-strong/50' : 'bg-primary')}
              style={{ width: `${String(Math.round((source.count / max) * 100))}%` }}
            />
          </div>
          <span className="text-right font-mono text-small">{formatCount(source.count)}</span>
        </div>
      ))}
    </Card>
  )
}

/** Which view and pipeline the panel shows: only once there are numbers to show. */
function ViewControls({
  data,
  query,
  setQuery,
}: {
  data: VenueLeadStatsDto
  query: LeadStatsQuery
  setQuery: (next: LeadStatsQuery) => void
}) {
  return (
    <>
      <ViewToggle view={data.view} onView={(v) => setQuery({ ...query, view: v })} />
      {data.pipelines.length > 1 ? (
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted">
          Pipeline
          <Select size="sm" value={data.pipelineId ?? ''} onChange={(e) => setQuery({ ...query, pipeline: e.target.value })}>
            {data.pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
    </>
  )
}

/**
 * A sub-account GoHighLevel stopped answering. One that was read is counted as
 * it was then; one that stopped before its first read isn't counted at all.
 */
function NotWorking({ connection, canFix }: { connection: VenueLeadStatsDto['connections'][number]; canFix: boolean }) {
  if (connection.readAt === null) {
    return (
      <Callout tone="attention" title={`GoHighLevel isn’t working for ${connection.locationName}`}>
        Its leads aren’t counted yet.{canFix ? ' Replace its key in the venue’s settings to start counting them.' : ''}
      </Callout>
    )
  }
  return (
    <Callout tone="attention" title={`GoHighLevel stopped working for ${connection.locationName}`}>
      Its leads are counted as they were on {instantLabel(connection.readAt)}.
      {canFix ? ' Replace its key in the venue’s settings to bring them up to date.' : ''}
    </Callout>
  )
}

function UpdatedLine({ data }: { data: VenueLeadStatsDto }) {
  if (data.reading) return <p className="m-0 text-small text-ink-muted">From GoHighLevel · updating now…</p>
  if (data.updatedAt === null) return <p className="m-0 text-small text-ink-muted">From GoHighLevel</p>
  return (
    <p className={cn('m-0 text-small', data.stale ? 'text-hold-ink' : 'text-ink-muted')}>
      From GoHighLevel · updated {instantLabel(data.updatedAt)}
      {data.stale ? '. These may be out of date.' : ''}
    </p>
  )
}

/**
 * The venue's lead stats on its dashboard (docs/gohighlevel-stats.spec.md, "The
 * Leads panel"; mockup https://claude.ai/artifact/LgHEgkXuFTNVqkgLnt7LmF). Nothing
 * at all for a venue with no GoHighLevel connection.
 */
export function LeadsPanel({ venueId }: { venueId: string }) {
  const { user } = useAuth()
  const { toast } = useToast()
  const [query, setQuery] = useLeadStatsQuery()
  const statsQuery = useVenueLeadStats(venueId, query, true)
  const refresh = useRefreshLeadStats(venueId)
  const data = statsQuery.data

  if (statsQuery.isPending) return <DetailSkeleton />
  if (statsQuery.isError) return <ErrorState message={requestErrorMessage(statsQuery.error)} onRetry={() => void statsQuery.refetch()} />
  if (data === undefined || data.connections.length === 0) return null

  const view: LeadStatsView = data.view
  const counts = data.numbers
  const currency = data.spend?.currencyCode ?? null
  const failing = data.connections.filter((c) => c.failingSince !== null)
  const settingsLink = data.canRefresh && reachesCompany(user) ? '/gohighlevel' : `/venues/${venueId}/settings#lead-stats`
  const anyMoving = view === 'cohort' && counts.some((n) => n.stillMoving)
  const calendarGap = counts.some((n) => n.kind === 'appointments' && (n.countedFrom !== null || n.trend.some((p) => p.value === null)))

  return (
    <section aria-labelledby="leads-heading" className="space-y-5 rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h2 id="leads-heading" className="m-0 text-heading font-semibold text-ink">
            Leads
          </h2>
          <UpdatedLine data={data} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.window === null ? null : <ViewControls data={data} query={query} setQuery={setQuery} />}
          {data.canRefresh ? (
            <Button
              variant="outline"
              size="sm"
              disabled={data.reading || refresh.isPending}
              onClick={() =>
                refresh.mutate(undefined, {
                  onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }),
                })
              }
            >
              <RotateCw className={cn('h-3.5 w-3.5', (data.reading || refresh.isPending) && 'animate-spin')} />
              {data.reading ? 'Updating…' : 'Refresh'}
            </Button>
          ) : null}
        </div>
      </div>

      {failing.map((c) => (
        <NotWorking key={c.id} connection={c} canFix={data.canRefresh} />
      ))}

      {data.pipelines.length === 0 ? (
        <p className="m-0 text-sm text-ink-muted">
          No numbers are defined yet.{' '}
          {data.canRefresh ? (
            <Link to="/gohighlevel" className="font-medium text-accent-ink hover:underline">
              Define them on the GoHighLevel page
            </Link>
          ) : (
            'An organization admin defines them.'
          )}
        </p>
      ) : null}

      {data.firstReadPending ? (
        <p className="m-0 text-sm text-ink-muted">
          Reading this venue’s GoHighLevel for the first time. Its numbers appear here when that’s done.
        </p>
      ) : null}

      {data.window === null ? null : (
        <>
          <PeriodBar
            window={data.window}
            periods={data.periods}
            spend={data.spend}
            onPeriod={(start) => setQuery({ ...query, period: start })}
          />

          <div className="flex flex-wrap gap-3">
            {counts.map((n) => (
              <NumberTile key={n.metricId} number={n} currency={currency} />
            ))}
          </div>

          {anyMoving ? (
            <InfoNote className="-mt-1.5">
              Couples who wrote in during the last eight weeks are still touring and booking, so those periods’ numbers will rise. Their
              bars are lighter.
            </InfoNote>
          ) : null}

          {calendarGap ? (
            <InfoNote className="-mt-1.5">
              Numbers counted from calendars start with their calendars’ first appointment. GoHighLevel has nothing on a calendar before
              then, so earlier periods show a dash.
            </InfoNote>
          ) : null}

          {view === 'activity' && data.recordingSince !== null ? (
            <InfoNote className="-mt-1.5">
              Steps a lead takes are counted from {formatAdDay(data.recordingSince)}. GoHighLevel doesn’t keep a record of when a lead moved
              before that; leads and appointments go back further because GoHighLevel dates them.
            </InfoNote>
          ) : null}

          <div className="flex flex-wrap gap-4">
            {data.conversions.length === 0 ? null : (
              <Card className="flex min-w-0 flex-[1_1_380px] flex-col p-4">
                <h3 className="m-0 mb-2 text-sm font-semibold text-ink">Conversions</h3>
                <ul className="m-0 list-none p-0">
                  {data.conversions.map((c) => (
                    <ConversionRow key={c.metricId} conversion={c} />
                  ))}
                </ul>
              </Card>
            )}
            <Sources sources={data.sources} />
          </div>

          {data.split === null ? null : <Split data={data} />}
        </>
      )}

      <div className="flex flex-wrap justify-between gap-2 text-small text-ink-muted">
        <span>
          Two-week periods from Sunday, in the venue’s time zone.{' '}
          {view === 'cohort'
            ? 'Couples count in the period they first wrote in, and appointments in the period they were booked or held.'
            : 'Each number counts what happened during the period.'}
        </span>
        {/* Without Edit the venue's settings show what each number is made of, to read. */}
        <Link to={settingsLink} className="font-medium text-accent-ink hover:underline">
          {data.canRefresh || can(user, 'leadStats:edit') ? 'Change what each number counts' : 'See what each number counts'}
        </Link>
      </div>
    </section>
  )
}

/** Each sub-account's numbers on their own, at a venue with more than one. */
function Split({ data }: { data: VenueLeadStatsDto }) {
  const split = data.split ?? []
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="w-full text-left text-control">
        <thead className="bg-surface-sunken text-xs text-ink-muted">
          <tr>
            <th className="px-3 py-2 font-medium">By sub-account</th>
            {data.numbers.map((n) => (
              <th key={n.metricId} className="px-3 py-2 text-right font-medium">
                {n.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {split.map((row) => (
            <tr key={row.connectionId} className="border-t border-line">
              <td className="px-3 py-2 text-ink">{row.locationName}</td>
              {data.numbers.map((n) => {
                const value = row.values.find((v) => v.metricId === n.metricId)?.value ?? null
                return (
                  <td key={n.metricId} className="px-3 py-2 text-right font-mono">
                    {value === null ? '—' : formatCount(value)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="m-0 border-t border-line px-3 py-2 text-xs text-ink-muted">
        A couple who wrote to more than one sub-account counts once in the venue’s numbers and once in each sub-account here.
      </p>
    </div>
  )
}
